import {useCallback, useEffect, useState} from "react";

import formatDate from "helpers/formatDate";
import AppointmentsService from "services/appointments";
import formatTime from "helpers/formatTime";
import {sessionUkTimes, toTwelveHourTime, ukOffsetMinutes} from "helpers/ukDate";

const getAvailablePrioritiesOptions = ({ totalPriorities = 5, takenPriorities }) => {
    const priorityOptions = [];
    new Array(totalPriorities).fill().forEach((acc, index) => {
        const priority = index + 1;
        priorityOptions.push({
            value: priority,
            label: priority,
            isDisabled: takenPriorities.find(option => option.priority === priority)
        });
    });

    return priorityOptions;
};

export const useSessionCreateAndManage = ({ currentSession, isEdit }) => {
    // An existing session's day and times in UK time (from its slots), as the modal shows them.
    const current = currentSession ? sessionUkTimes(currentSession) : null;
    const [isLoading, setIsLoading] = useState(false);
    const [isSessionDurationDisabled, setIsSessionDurationDisabled] = useState(!isEdit);
    const [isPriorityFieldDisabled, setIsPriorityFieldDisabled] = useState(!isEdit);
    const [sessionParams, setSessionParams] = useState({
        doctorId: currentSession?.doctorId || "",
        startDate: current ? (current.date || currentSession.startDate || "") : "",
        endDate: current ? (current.date || currentSession.endDate || "") : "",
        startsAt: current ? toTwelveHourTime(current.startsAt) : "",
        endsAt: current ? toTwelveHourTime(current.endsAt) : ""
    });
    const [takenTimeSlots, setTakenTimeSlots] = useState([]);
    const [availablePrioritiesOptions, setAvailablePriorities] = useState([]);

    const handleSessionValidityChange = useCallback((start, end) => {
        setSessionParams(prevValue => {
            return {
                ...prevValue,
                startDate: formatDate(start),
                endDate: formatDate(end)
            }
        });
    }, []);

    const getTakenTimeSlots = async () => {
        if (!sessionParams.doctorId || !sessionParams.startDate || !sessionParams.endDate || sessionParams.endDate === "Invalid date") return;

        setIsLoading(true);

        // eslint-disable-next-line no-unused-vars
        const { startsAt, endsAt, ...requestParams } = sessionParams;

        const modifiedRequestParams = isEdit ? {
            ...requestParams,
            withoutCurrent: true,
            sessionId: currentSession?.id
        } : requestParams;

        try {
            const response = await AppointmentsService.getTakenTimeSlots(modifiedRequestParams);

            setTakenTimeSlots(response.data);
            setIsSessionDurationDisabled(false);

        } catch {
            //empty
        } finally {
            setIsLoading(false);
        }
    }


    useEffect(() => {
        getTakenTimeSlots();
    }, [sessionParams.doctorId, sessionParams.startDate, sessionParams.endDate]);

    const getAvailablePriorities = async () => {
        if (!sessionParams.doctorId
            || !sessionParams.startDate
            || !sessionParams.endDate
            || sessionParams.endDate === "Invalid date"
            || !sessionParams.startsAt
            || !sessionParams.endsAt
        ) return;

        setIsLoading(true);

        // eslint-disable-next-line no-unused-vars
        const { appointmentDuration, doctorId, ...requestParams } = sessionParams;

        const modifiedRequestParams = isEdit ? {
            ...requestParams,
            withoutCurrent: true,
            sessionId: currentSession?.id
        } : requestParams;

        try {
            const response = await AppointmentsService.getTakenPriorities({
                ...modifiedRequestParams,
                startsAt: formatTime(sessionParams.startsAt),
                endsAt: formatTime(sessionParams.endsAt),
                // The UK offset on the session's first day (BST or GMT), not today's.
                timezoneOffset: ukOffsetMinutes(sessionParams.startDate, formatTime(sessionParams.startsAt))
            });


            const availablePriorities = getAvailablePrioritiesOptions({
                totalPriorities: response.totallyCountOfPriority,
                takenPriorities: response.data
            });

            setAvailablePriorities(availablePriorities);

            setIsPriorityFieldDisabled(false);

        } catch {
            //empty
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getAvailablePriorities();
    }, [sessionParams]);

    return {
        isSessionDurationDisabled,
        isPriorityFieldDisabled,
        takenTimeSlots,
        availablePrioritiesOptions,
        setSessionParams,
        handleSessionValidityChange,
        isLoading
    };

};
